import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AddNewConsultationZone from "../AddNewConsultationZone/AddNewConsultationZone";
import RecentMedicalVisits from "../RecentMedicalVisits/RecentMedicalVisits";
import DocumentsTab from "../DocumentsTab/DocumentsTab";
import PaymentTab from "../PaymentTab/PaymentTab";
import ToolsTab from "../toolsTab/ToolsTab";
import NephrologueConsultationPage from "../../../../specialty/neurologue/pages/NeurologueConsultationPage/NeurologueConsultationPage";
import PulmonologyConsultationPage from "../../../../specialty/pulmonology/pages/PulmonologyConsultationPage/PulmonologyConsultationPage";
import PulmonologyHistory from "../../../../specialty/pulmonology/components/PulmonologyHistory/PulmonologyHistory";
import { selectPatientConsultations } from "../../../../consultation/redux/selectors/consultationSelectors";
import { fetchConsultationsByPatientId } from "../../../../consultation/redux/thunks/consultationThunks";
import { DOCTOR_ROUTES } from "../../../../routes/patientRoutes/routePaths";
import "./ConsultationTabs.css";

export default function ConsultationTabs({
  userId,
  medicalInfo = null,
  patientMeasurements = null,
  patientInfo = null,
  paymentPatientId = userId,
  subprofileId = null,
  doctorId,
  consultations = [],
  sharedFiles = [],
  onSuccess,
  onOpenPrescribePopup,
  onCancelConsultation,
  initialTab = "consultation",
  specialty = "general",
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [lastConsultationId, setLastConsultationId] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState({
    consultationPrice: 0,
    medicalActsTotal: 0,
    total: 0,
  });

  const patientConsultations = useSelector(selectPatientConsultations);

  useEffect(() => {
    setActiveTab("consultation");
  }, [specialty]);

  const historyConsultations =
    Array.isArray(consultations) && consultations.length > 0
      ? consultations
      : patientConsultations;

  // Utilise l'id de la session courante, sinon la plus récente en BD
  const resolvedConsultationId =
    lastConsultationId ||
    (Array.isArray(patientConsultations) && patientConsultations.length > 0
      ? patientConsultations[0].id
      : null);

  const tabs = [
    { key: "consultation", label: t("consultationPage.tabs.consultation") },
    { key: "history", label: t("consultationPage.tabs.history") },
    { key: "documents", label: t("consultationPage.tabs.documents") },
    { key: "payment", label: t("consultationPage.tabs.payment") },
    { key: "tools", label: t("consultationPage.tabs.tools", { defaultValue: "Tools" }) },
  ];

  const handleSuccess = async (consultationId) => {
    if (consultationId) setLastConsultationId(consultationId);
    await onSuccess?.();
  };

  const handleTotalChange = useCallback((data) => {
    if (typeof data === "number") {
      setPaymentSummary({ consultationPrice: data, medicalActsTotal: 0, total: data });
      return;
    }
    setPaymentSummary({
      consultationPrice: Number(data?.consultationPrice || 0),
      medicalActsTotal: Number(data?.medicalActsTotal || 0),
      total: Number(data?.total || 0),
    });
  }, []);

  const handleTabClick = (key) => {
    if (key === "payment" && paymentPatientId) {
      const query = new URLSearchParams();
      if (subprofileId) query.set("subprofileId", subprofileId);
      const returnQuery = new URLSearchParams(location.search);
      returnQuery.set("specialty", specialty);
      const returnSearch = returnQuery.toString();
      const returnTo = `${location.pathname}${returnSearch ? `?${returnSearch}` : ""}`;
      query.set("returnTo", returnTo);
      const suffix = query.toString() ? `?${query.toString()}` : "";
      navigate(
        `${DOCTOR_ROUTES.PATIENT_PAYMENT_HISTORY(paymentPatientId)}${suffix}`,
        { state: { returnTo } },
      );
      return;
    }
    setActiveTab(key);
    if (key === "history" && userId) {
      dispatch(fetchConsultationsByPatientId(userId));
    }
  };

  return (
    <section className="consultation-tabs">
      <div className="tabs-header" role="tablist" aria-label={t("consultationPage.tabs.consultation")}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? "active" : ""}
            onClick={() => handleTabClick(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tabs-content">
        {activeTab === "consultation" && (
          specialty === "pulmonology" ? (
            <PulmonologyConsultationPage
              patientId={userId}
              onContinueToStep2={(initialDocumentKey) => onOpenPrescribePopup?.(null, { frontOnly: true, initialDocumentKey })}
              onCancelConsultation={onCancelConsultation}
            />
          ) : specialty === "nephrologue" || specialty === "neurologue" ? (
            <NephrologueConsultationPage
              userId={userId}
              medicalInfo={medicalInfo}
              patientMeasurements={patientMeasurements}
              doctorId={doctorId}
              subprofileId={subprofileId}
              onSuccess={handleSuccess}
              onOpenPrescribePopup={onOpenPrescribePopup}
            />
          ) : (
            <AddNewConsultationZone
              userId={userId}
              doctorId={doctorId}
              onSuccess={handleSuccess}
              onOpenPrescribePopup={onOpenPrescribePopup}
              onTotalChange={handleTotalChange}
              onAddDocument={() => {
                if (!userId) return;
                navigate(`${DOCTOR_ROUTES.DOCTOR_PATIENT_VIEW(userId)}/${DOCTOR_ROUTES.MEDICAL_RECORD_DECOR}`);
              }}
            />
          )
        )}

        {activeTab === "history" && (
          specialty === "pulmonology" ? (
            <PulmonologyHistory patientId={userId} />
          ) : (
            <RecentMedicalVisits
              consultations={historyConsultations}
              doctorId={doctorId}
            />
          )
        )}

        {activeTab === "documents" && (
          <DocumentsTab userId={userId} doctorId={doctorId} sharedFiles={sharedFiles} />
        )}

        {activeTab === "payment" && (
          <PaymentTab
            consultationId={resolvedConsultationId}
            patientId={paymentPatientId}
            subprofileId={subprofileId}
            onPaid={() => {}}
          />
        )}

        {activeTab === "tools" && (
          <ToolsTab
            key={userId || "patient"}
            patientMeasurements={patientMeasurements}
            patientInfo={patientInfo}
          />
        )}
      </div>
    </section>
  );
}
